"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  MoreVertical,
  Edit,
  Trash,
  Eye,
  Users,
  Clock,
  DollarSign,
  Play,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/admin/shared/status-badge";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import { ROLES, type RoleId } from "@/lib/constants/roles";
import { richTextToPlainText } from "@/lib/utils/rich-text";
import { getCurrencySymbol } from "@/lib/utils/currency";

interface Show {
  id: number;
  venue_id: number;
  duration_minutes: number;
  min_participants: number;
  max_participants: number;
  status?: string;
  status_id?: number;
  statuses?: {
    id: number;
    name: string;
  };
  customer_types?: {
    id: number;
    name: string;
  };
  show_details?: Array<{
    id: number;
    name: string;
    details: string;
    duration?: number;
    lang: string;
  }>;
  show_studios?: Array<{
    studio_id: number;
    studios?: {
      id: number;
      name: string;
    };
  }>;
  show_medias?: Array<{
    id?: number;
    url: string;
    type: string;
    is_primary: boolean;
    alt_text?: string;
  }>;
  show_prices?: Array<{
    id?: number;
    price_type: string;
    amount: number;
    currency: string;
    pricing?: {
      id: number;
      price_type: string;
      amount: number;
      currency: string;
    };
  }>;
  // Booking statistics
  bookings_count?: number;
  monthly_bookings?: number;
  total_revenue?: number;
  average_rating?: number;
}

interface ShowCardProps {
  show: Show;
  venueId: number;
  onDelete?: (showId: number) => void;
  userRoleId?: RoleId;
}

export function ShowCard({
  show,
  venueId,
  onDelete,
  userRoleId = ROLES.BOOKING_STAFF,
}: ShowCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  console.log("Show data:", show); // Debug log
  console.log("All show details:", show.show_details); // Debug log

  const primaryDetail =
    show.show_details?.find((d) => d.lang === "en") || show.show_details?.[0];
  const primaryMedia =
    show.show_medias?.find((m) => m.is_primary) || show.show_medias?.[0];
  // Debug show_studios structure
  console.log("Show studios debug:", {
    showId: show.id,
    show_studios: show.show_studios,
    studioData: show.show_studios?.map((ss) => ({
      studio_id: ss.studio_id,
      studios: ss.studios,
    })),
  });

  const studios =
    show.show_studios
      ?.map((ss) => {
        // Handle different data structures
        if (ss.studios?.name) {
          return ss.studios.name;
        }
        // Fallback: if only studio_id is available, show it with a prefix
        if (ss.studio_id) {
          return `Studio ${ss.studio_id}`;
        }
        return "Unknown Studio";
      })
      .join(", ") || "No studios assigned";

  // Get duration from show_details table, fallback to show.duration_minutes
  const duration = primaryDetail?.duration || show.duration_minutes || 0;

  // Extract show name from details field (usually the first part before the dash or period)
  const extractNameFromDetails = (details: string) => {
    if (!details) return "Unnamed Show";

    // Split on ". " (period followed by space) - this is how we save it
    const parts = details.split(". ");
    if (parts.length > 1) {
      return parts[0].trim();
    }

    // If no ". " pattern, return the whole string as name
    return details.trim();
  };

  // Extract description from details field (everything after the first ". ")
  const extractDescriptionFromDetails = (details: string) => {
    if (!details) return "";

    // Split on ". " (period followed by space) - this is how we save it
    const parts = details.split(". ");
    if (parts.length > 1) {
      // Join the rest back together in case there are multiple ". " in the description
      return parts.slice(1).join(". ").trim();
    }

    // If no ". " pattern, return empty string (just show the name)
    return "";
  };

  const showName = primaryDetail?.details
    ? extractNameFromDetails(primaryDetail.details)
    : "Unnamed Show";

  const showDescriptionHtml = primaryDetail?.details
    ? extractDescriptionFromDetails(primaryDetail.details)
    : "";

  // Convert HTML to plain text for display in card
  const showDescription = richTextToPlainText(showDescriptionHtml);

  console.log("Primary detail:", primaryDetail); // Debug log
  console.log("Primary detail description:", primaryDetail?.details); // Debug log
  console.log("Primary detail duration:", primaryDetail?.duration); // Debug log
  console.log("Show duration_minutes:", show.duration_minutes); // Debug log
  console.log("Final duration used:", duration); // Debug log
  console.log("Studios:", studios); // Debug log

  // Get price range
  console.log("All show prices:", show.show_prices); // Debug log

  // Handle pricing structure - check if it's direct or nested
  const basePrices =
    show.show_prices
      ?.map((sp) => sp.pricing || sp) // Extract pricing object if nested, otherwise use sp directly
      ?.filter((p) => p && p.price_type === "base") || [];

  console.log("Base prices:", basePrices); // Debug log

  // Fallback pricing logic - if no pricing data exists, show sample pricing based on show type
  let priceRange;
  if (basePrices.length > 0) {
    const currencySymbol = getCurrencySymbol(basePrices[0].currency || "USD");
    priceRange =
      basePrices.length === 1
        ? `${currencySymbol}${basePrices[0].amount}`
        : `${currencySymbol}${Math.min(...basePrices.map((p) => p.amount))} - ${currencySymbol}${Math.max(...basePrices.map((p) => p.amount))}`;
  } else {
    // Show default pricing based on customer type or participants
    const customerType = show.customer_types?.name?.toLowerCase();
    if (customerType === "corporate") {
      priceRange = `$${50 * show.min_participants} - $${75 * show.max_participants}`;
    } else if (customerType === "private") {
      priceRange = `$${30 * show.min_participants} - $${50 * show.max_participants}`;
    } else {
      priceRange = "Contact for pricing";
    }
  }

  console.log("Calculated price range:", priceRange); // Debug log

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      onDelete(show.id);
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  const canDelete = userRoleId === ROLES.SUPER_ADMIN;

  return (
    <Card className="group hover:shadow-md transition-shadow">
      <CardHeader className="p-0">
        <div className="relative h-48 bg-gray-200 rounded-t-lg overflow-hidden">
          {primaryMedia ? (
            primaryMedia.type === "video" ? (
              <div
                className="relative w-full h-full group cursor-pointer"
                onMouseEnter={() => {
                  const video = document.querySelector(
                    `[data-video-id="show-${show.id}"]`,
                  ) as HTMLVideoElement;
                  if (video) {
                    console.log("Attempting to play video for show:", show.id);
                    video.currentTime = 0;
                    video
                      .play()
                      .then(() => {
                        console.log("Video playing successfully");
                      })
                      .catch((error) => {
                        console.error("Video autoplay failed:", error);
                      });
                  }
                }}
                onMouseLeave={() => {
                  const video = document.querySelector(
                    `[data-video-id="show-${show.id}"]`,
                  ) as HTMLVideoElement;
                  if (video) {
                    console.log("Pausing video for show:", show.id);
                    video.pause();
                    video.currentTime = 0;
                  }
                }}
              >
                <video
                  data-video-id={`show-${show.id}`}
                  src={primaryMedia.url}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  preload="auto"
                  onCanPlay={() => console.log("Video can play:", show.id)}
                  onError={(e) => console.error("Video error:", e)}
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-200" />
                <div className="absolute inset-0 group-hover:opacity-0 transition-opacity duration-200 flex items-center justify-center">
                  {/* <div className="bg-white/90 rounded-full p-3">
                    <Play className="h-6 w-6 text-gray-800" />
                  </div> */}
                </div>
              </div>
            ) : (
              <Image
                src={primaryMedia.url}
                alt={showName || "Show media"}
                fill
                className="object-cover"
              />
            )
          ) : (
            <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200">
              <Users className="h-12 w-12 text-gray-400" />
            </div>
          )}

          <div className="absolute top-3 left-3">
            <StatusBadge
              status={show.statuses?.name || show.status}
              size="sm"
            />
          </div>

          <div className="absolute top-3 right-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 bg-white/80 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link href={`/superadmin/venue/${venueId}/shows/${show.id}`}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Link>
                </DropdownMenuItem>

                {canDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4">
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-lg truncate">{showName}</h3>
            {showDescription && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                {showDescription}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{formatDuration(duration)}</span>
            </div>

            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>
                {show.min_participants}-{show.max_participants}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{priceRange}</span>
            </div>

            <div className="text-xs text-muted-foreground">
              {show.customer_types?.name || "No type"}
            </div>
          </div>

          <div className="pt-2 border-t">
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Studios:</span> {studios}
            </div>
          </div>

          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>
              {show.monthly_bookings !== undefined
                ? `${show.monthly_bookings} booking${show.monthly_bookings !== 1 ? "s" : ""} this month`
                : "No bookings yet"}
            </span>
            <span>
              {show.average_rating !== undefined
                ? `⭐ ${show.average_rating.toFixed(1)}`
                : "⭐ No ratings"}
            </span>
          </div>
        </div>
      </CardContent>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Show"
        description="Are you sure you want to delete this show? This action cannot be undone."
        itemName={showName}
        itemSubtext={`${show.min_participants}-${show.max_participants} participants • ${formatDuration(duration)}`}
        confirmText="Delete Show"
        confirmLoadingText="Deleting..."
        isLoading={isDeleting}
      />
    </Card>
  );
}
